import { SCROLL_CLASS_NAMES } from "../../models/scroll/ScrollHTMLClass";
import { ScrollMarkRule } from "../../models/scroll/ScrollTriggerRule";
import { StringContext } from "../StringContext";
// import { logScrollDebug } from "../../utils/scroll-debug";

export class ScrollController {
  protected context: StringContext;
  protected document: Document;

  public name: string = "";
  public isProg: boolean = false;
  public isParallaxEnabled: boolean = false;

  protected _isVertical: boolean = true;

  protected _scrollDirState: number = -1;
  protected _lastAppliedDirState: number = -1;
  protected isLastBottomScrollDirection: boolean = true;

  protected scrollTriggerRules: Array<ScrollMarkRule> = [];
  protected isActive: boolean = false;

  public set scrollDirection(dir: "vertical" | "horizontal") {
    this._isVertical = dir === "vertical";
  }

  constructor(context: StringContext) {
    this.document = document;
    this.context = context;
  }

  public onChangeDirection = () => {};
  public onScrollStart = () => {};
  public onScrollStop = () => {};

  public onCalcUpdate(): void {
    if (!this.isActive) return;

    const container = this.context.data.scroll.scrollContainer;
    const current = this.context.data.scroll.current;

    if (container) {
      if (this._isVertical) {
        container.scrollTo({ top: current, left: 0, behavior: "auto" });
      } else {
        container.scrollTo({ left: current, top: 0, behavior: "auto" });
      }
    }

    if (this._isVertical) {
      this.triggerScrollRules();
    }
  }

  public onFrame(): void {}
  public onWheel(e: any): void {}
  public onScroll(e: any): void {}
  public onTouchStart(e: TouchEvent): void {}
  public onTouchMove(e: TouchEvent): void {}
  public onTouchEnd(e: TouchEvent): void {}
  public disableScrollEvents(): void {}
  public enableScrollEvents(): void {}

  public activate(): void {
    if (this.isActive) return;
    this.isActive = true;
    this.enableScrollEvents();
  }

  public deactivate(): void {
    if (!this.isActive) return;
    this.isActive = false;
    this.disableScrollEvents();
    this.isProg = false;

    const scrollData = this.context.data.scroll;
    scrollData.target = scrollData.current;
    scrollData.delta = 0;
    scrollData.lerped = 0;
    scrollData.displacement = 0;

    this.clearScrollingClasses();
    this._scrollDirState = -1;
    this._lastAppliedDirState = -1;
    this.onScrollStop();
  }

  public destroy(): void {}

  protected updateScrollDirection(isDown: boolean) {
    this.isLastBottomScrollDirection = isDown;
    const newState = isDown ? 1 : 0;

    if (this._scrollDirState === -1) {
      this._scrollDirState = newState;
      return;
    }

    this._scrollDirState = newState;
    this.context.data.scroll.isScrollingDown = isDown;
    this.onChangeDirection();
    this.context.events.emit(`scroll:direction:change`, isDown);

    if (this.context.settings["global-class"] && this._lastAppliedDirState !== newState) {
      const rootClasses = document.documentElement.classList;

      if (isDown) {
        rootClasses.remove(
          SCROLL_CLASS_NAMES.SCROLLING_BACKWARD,
          SCROLL_CLASS_NAMES.SCROLL_BACKWARD,
        );
        rootClasses.add(SCROLL_CLASS_NAMES.SCROLLING_FORWARD, SCROLL_CLASS_NAMES.SCROLL_FORWARD);
      } else {
        rootClasses.remove(SCROLL_CLASS_NAMES.SCROLLING_FORWARD, SCROLL_CLASS_NAMES.SCROLL_FORWARD);
        rootClasses.add(SCROLL_CLASS_NAMES.SCROLLING_BACKWARD, SCROLL_CLASS_NAMES.SCROLL_BACKWARD);
      }

      this._lastAppliedDirState = newState;
    }
  }

  protected clearScrollingClasses() {
    document.documentElement.classList.remove(
      SCROLL_CLASS_NAMES.SCROLLING_BACKWARD,
      SCROLL_CLASS_NAMES.SCROLLING_FORWARD,
      SCROLL_CLASS_NAMES.SCROLL_BACKWARD,
      SCROLL_CLASS_NAMES.SCROLL_FORWARD,
    );
  }

  protected triggerScrollRules() {
    const rules = this.scrollTriggerRules;
    const len = rules.length;
    const currentScroll = this.context.data.scroll.current;
    const isDown = this.isLastBottomScrollDirection;

    for (let i = 0; i < len; i++) {
      const rule = rules[i];

      const directionMatches =
        rule.direction === "any" ||
        (isDown && rule.direction === "forward") ||
        (!isDown && rule.direction === "backward");

      const shouldTrigger = directionMatches && currentScroll >= rule.offset;

      if (shouldTrigger && !rule.isActive) {
        rule.isActive = true;
        rule.onEnter?.();
        if (rule.toggleClass) {
          rule.toggleClass.target.classList.add(rule.toggleClass.className);
        }
      } else if (!shouldTrigger && rule.isActive) {
        rule.isActive = false;
        rule.onLeave?.();
        if (rule.toggleClass) {
          rule.toggleClass.target.classList.remove(rule.toggleClass.className);
        }
      }
    }
  }

  public addScrollMark(rule: ScrollMarkRule) {
    this.scrollTriggerRules.push(rule);
  }

  public removeScrollMark(id: string) {
    const rules = this.scrollTriggerRules;
    for (let i = 0; i < rules.length; i++) {
      if (rules[i].id === id) {
        rules.splice(i, 1);
        break;
      }
    }
  }

  public scrollTo(position: number, immediate?: boolean): void {}
}
